import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { apiRequest } from "@/utils/api-request";
import { Download, Eye, School, FileText, Search } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface VisibilityData {
  slate_id: number;
  slate_name: string;
  city_name: string;
  area_name: string;
  blog_count: number;
  total_views: number;
}

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

const VisibilityReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState<VisibilityData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter schools based on search term (searches name, city, area)
  const filteredSchools = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(school =>
      school.slate_name?.toLowerCase().includes(term) ||
      school.city_name?.toLowerCase().includes(term) ||
      school.area_name?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("start_date", fromDate);
      if (toDate) params.append("end_date", toDate);

      const response = await apiRequest(`/api/reports/visibility?${params.toString()}`, {
        method: "GET",
      });

      if (response.success && response.data) {
        setData(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching visibility report:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["School ID", "School Name", "City", "Area", "Blogs", "Total Views"];
    const csvRows = [headers.join(",")];
    data.forEach((s) => {
      csvRows.push([
        s.slate_id,
        `"${s.slate_name}"`,
        `"${s.city_name || ""}"`,
        `"${s.area_name || ""}"`,
        s.blog_count,
        s.total_views
      ].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `visibility-report-${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalSchools = data.length;
  const totalBlogs = data.reduce((sum, s) => sum + (s.blog_count || 0), 0);
  const totalViews = data.reduce((sum, s) => sum + (s.total_views || 0), 0);

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader
          title="Visibility Report"
          subtitle="Schools mentioned in blogs with view counts"
          icon={<Eye className="h-5 w-5 text-white" />}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onApplyFilter={fetchData}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader
        title="Visibility Report"
        subtitle="Schools mentioned in blogs with view counts"
        icon={<Eye className="h-5 w-5 text-white" />}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onApplyFilter={fetchData}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Total Schools</div>
                  <div className="text-2xl font-bold">{formatNumber(totalSchools)}</div>
                </div>
                <div className="p-2 bg-brandColor rounded-lg">
                  <School className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Total Blogs</div>
                  <div className="text-2xl font-bold">{formatNumber(totalBlogs)}</div>
                </div>
                <div className="p-2 bg-blue-500 rounded-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Total Views</div>
                  <div className="text-2xl font-bold">{formatNumber(totalViews)}</div>
                </div>
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Eye className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Visibility Table */}
          <div className="background-container rounded-lg shadow">
            <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Eye className="h-5 w-5" />
                School Visibility in Blogs
              </h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search school, city, area..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-64"
                  />
                </div>
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-brandColor text-sm font-medium rounded-lg hover:bg-gray-100"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
              </div>
            </div>
            <div className="px-4 pb-4 overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">School ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">School Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">City</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Area</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-gray-50">Blogs</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-gray-50">Total Views</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSchools.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No data found
                      </td>
                    </tr>
                  ) : (
                    filteredSchools.map((school) => (
                      <tr key={school.slate_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{school.slate_id}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{school.slate_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{school.city_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{school.area_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {school.blog_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-purple-600">
                          {formatNumber(school.total_views)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisibilityReport;
